import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { BehaviorSubject, Observable, Subject, distinctUntilChanged } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { keepState } from '@fcom/rx';
import { addClass, isVisible, removeClass } from '@fcom/core/utils';
import { AirOffersStatus } from '@fcom/common/interfaces/booking';

import { AncillaryLoaderStatus } from '../../modules/ancillaries/enums';

/**
 * A subview component that displays its content in a scrollable div.
 *
 * @example
 * <b>Example with buttons from parameters</b>
 * The button is rendered inside the scrollable content
 *
 * <code><pre>
 * <fin-subview #subview>
 *  <ng-container *ngIf="subview.visible$ | async">
 *    <h1>This is scrollable content</h1>
 *    <button (click)="subview.close()">This is inside the scrollable content</button>
 *    <pre>This is custom data passed in the <i>open</i> call: {{ subview.data$ | async | json }}</pre>
 *  </ng-container>
 * </fin-subview>
 * </code></pre>
 */

@Component({
  selector: 'fin-subview',
  styleUrls: ['subview.component.scss'],
  templateUrl: 'subview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SubviewComponent implements OnDestroy {
  elementRef = inject(ElementRef);
  private document = inject<Document>(DOCUMENT);
  private changeDetectorRef = inject(ChangeDetectorRef);

  /**
   * A little bit longer than the CSS animation to ensure that the element has slid down
   */
  readonly ANIMATION_DURATION_MS = 350;
  readonly subviewId = uuid();
  readonly AncillaryLoaderStatus = AncillaryLoaderStatus;
  readonly AirOffersStatus = AirOffersStatus;

  @Output() openChange = new EventEmitter<boolean>();

  @Input()
  subviewName: string;

  @Input()
  subviewContext: string;

  @Input()
  enableScroll = true;

  @Input()
  backgroundClass = 'white-bg surface-primary';

  @Input()
  ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus>;

  @Input()
  set open(_open: boolean) {
    const open = coerceBooleanProperty(_open);

    if (this._openState === open) {
      return;
    }
    this._openState = open;

    this.openChange.emit(this._openState);

    if (this._openState) {
      this.openSubview();
    } else {
      this.closeSubview(true);
    }
  }

  get open(): boolean {
    return this._openState;
  }

  /**
   * Called when the subview is closed after onConfirm, no matter how the subview was closed.
   */
  @Output()
  closeSubViewEvent: EventEmitter<void> = new EventEmitter();

  /**
   * Asking confirmation for close.
   */
  @Output()
  shouldCloseSubview: EventEmitter<void> = new EventEmitter();

  subViewVisible$: Subject<boolean> = new BehaviorSubject(false);
  subViewVisibleDelayed$: Observable<boolean>;
  labelledBy: string;

  private removeFocusListener: () => void;

  /**
   * Private boolean property storing the open state
   */
  private _openState = false;

  constructor() {
    this.labelledBy = `subview-${this.subviewId}-title`;
    this.subViewVisibleDelayed$ = this.subViewVisible$.pipe(
      distinctUntilChanged(),
      keepState(this.ANIMATION_DURATION_MS)
    );
  }

  get visible$(): Observable<boolean> {
    return this.subViewVisible$;
  }

  ngOnDestroy(): void {
    this.cleanUp();
    removeClass(this.document.body, 'subview-open');
    this.elementRef = null;
  }

  closeSubview(emitEvent: boolean): void {
    this.subViewVisible$.next(false);
    this.changeDetectorRef.detectChanges();
    this.cleanUp();
    removeClass(this.document.body, 'subview-open');
    if (emitEvent) {
      // This needs to happen after this.cleanUp() which is removing focus listening from this component
      this.closeSubViewEvent.emit();
    }
  }

  /**
   * Open the subview.
   * @return void
   */
  private openSubview(): void {
    this.subViewVisible$.next(true);
    this.bindEventListeners();
    this.changeDetectorRef.detectChanges();
    this.onModalFocus(null, true);
    addClass(this.document.body, 'subview-open');
  }

  private bindEventListeners(): void {
    const listener = (event) => {
      this.onModalFocus(event);
    };
    this.document.addEventListener('focus', listener, true);
    this.document.addEventListener('focusin', listener);
    this.removeFocusListener = () => {
      this.document.removeEventListener('focus', listener, true);
      this.document.removeEventListener('focusin', listener);
    };
  }

  private onModalFocus(event: FocusEvent | null, forceFocus = false): void {
    const el = this.elementRef?.nativeElement;
    if (!!el && (!el.contains(event?.target) || forceFocus)) {
      event?.stopPropagation();

      const focusSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusableElements: Element[] = Array.from((el as Element).querySelectorAll(focusSelector))
        .filter((e) => e instanceof HTMLElement)
        .filter(isVisible);

      if (focusableElements.length !== 0) {
        setTimeout(() => (focusableElements[0] as HTMLElement).focus(), 0);
      }
    }
  }

  private cleanUp() {
    this.cleanUpEventHandlers();
  }

  private cleanUpEventHandlers() {
    if (this.removeFocusListener) {
      this.removeFocusListener();
      this.removeFocusListener = undefined;
    }
  }
}
